import { NotificationOutlined } from '@ant-design/icons';
import { Badge, Button, Popover, Tour, TourProps, theme } from 'antd';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import useEventListener from '../../hooks/useEventListener';
import useToggle from '../../hooks/useToggle';
import { BulkTicketChangeJob } from '../../services/tickets/tickets';
import { StorageHelperKeys, getLocalStorageItem, setLocalStorageItem } from '../../utils/storageHelpers';
import { filterSWRKeys } from '../../utils/swr';
import { JobStatusTypeMap } from '../Pages/AdminPage/FiltersView/ExportFiltersView/ExportFiltersTable';
import JobFeedbackList from './components/JobFeedbackList';
import { getJobFeedbacksOnStorage } from './utils';

export default function JobFeedbackContainer() {
    const {
        token: { motionEaseInOutCirc, paddingSM }
    } = theme.useToken();
    const { t } = useTranslation('common');
    const {
        query: { page, size, filterId }
    } = useRouter();
    const { mutate, cache } = useSWRConfig();

    const feedbackButtonRef = useRef<HTMLButtonElement>(null);
    const badgeRef = useRef<HTMLSpanElement>(null);

    const [jobs, setJobs] = useState<BulkTicketChangeJob[]>(() => getJobFeedbacksOnStorage() ?? []);
    const [isDotVisible, setIsDotVisible] = useState(false);

    const [popoverVisible, togglePopover] = useToggle();
    const [isTourOpen, setIsTourOpen] = useState(false);

    const isJobFeedbackNeeded = !!jobs?.find(({ status }) =>
        [JobStatusTypeMap.INITIALIZING, JobStatusTypeMap.RUNNING].includes(status)
    );

    useEffect(() => {
        setLocalStorageItem(StorageHelperKeys.ACTIVE_BULK_TICKET_CHANGE_JOB, isJobFeedbackNeeded);
    }, [isJobFeedbackNeeded]);

    useEventListener(
        'BULK_TICKET_CHANGE_JOB_START_EVENT',
        (e: WindowEventMap['BULK_TICKET_CHANGE_JOB_START_EVENT']) => {
            const jobId = e.detail.jobId;
            jobId && setJobs(getJobFeedbacksOnStorage());

            setIsDotVisible(!popoverVisible);

            getLocalStorageItem(StorageHelperKeys.NEW_USER) && setIsTourOpen(true);
        }
    );

    useEventListener(
        'BULK_TICKET_CHANGE_JOB_FINISH_EVENT',
        (e: WindowEventMap['BULK_TICKET_CHANGE_JOB_FINISH_EVENT']) => {
            if (e.detail?.isFinished) {
                setJobs(getJobFeedbacksOnStorage());

                for (const key of cache.keys()) {
                    if (
                        key.includes('/filters/') &&
                        !key.includes(`/filters/${filterId}`) && // prevent deleting cache which is related with current page, because below mutate function only works with already cached key
                        key.includes('?size=') &&
                        key.includes('page=')
                    ) {
                        cache.delete(key);
                    }
                }
                mutate(filterSWRKeys([`/filters/${filterId}?size=${+size}&page=${+page - 1}`, '/filters/counts']));

                setIsDotVisible(!popoverVisible);
            }
        }
    );

    const steps: TourProps['steps'] = [
        {
            title: t('bulkChangeFeedbackButton.title'),
            description: t('bulkChangeFeedbackButton.description'),
            target: () => feedbackButtonRef.current
        },
        {
            title: t('bulkChangeFeedbackButton.redDotTitle'),
            description: t('bulkChangeFeedbackButton.redDotDescription'),
            target: () => badgeRef.current.getElementsByTagName('sup')[0]
        }
    ];

    return (
        <>
            <Badge dot={isDotVisible} size="default" ref={badgeRef}>
                <Popover
                    overlayStyle={{
                        width: '50ch',
                        ...(!popoverVisible && { display: 'none' }),
                        animationTimingFunction: motionEaseInOutCirc
                    }}
                    overlayInnerStyle={{
                        paddingBottom: paddingSM,
                        paddingLeft: paddingSM,
                        paddingRight: 0,
                        paddingTop: paddingSM
                    }}
                    content={<JobFeedbackList jobs={jobs} />}
                    title={t('recentBulkTicketChangeJobs', { count: jobs.length })}
                    trigger="click"
                    open={true}
                    placement="bottomRight"
                    destroyTooltipOnHide={false}
                >
                    <Button
                        type="default"
                        icon={<NotificationOutlined />}
                        style={{ backgroundColor: 'transparent' }}
                        onClick={() => {
                            if (!isTourOpen) {
                                setIsDotVisible(false);
                                togglePopover();
                            }
                        }}
                        ref={feedbackButtonRef}
                    />
                </Popover>
            </Badge>
            <Tour
                open={isTourOpen}
                onClose={() => {
                    setIsTourOpen(false);
                    setLocalStorageItem(StorageHelperKeys.NEW_USER, false);
                }}
                steps={steps}
            />
        </>
    );
}
